import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { customToast } from "../../../../components/notify";

interface TabState {
    isDirty: boolean;
    saveHandler?: () => Promise<void>;
}

interface TabGuardContextValue {
    registerTab: (viewId: string | null, isDirty: boolean, saveHandler?: () => Promise<void>) => void;
    unregisterTab: (viewId: string | null) => void;
    checkBeforeSwitch: (fromView: string | null, toView: string | null) => Promise<boolean>;
}

interface TabGuardProviderProps {
    children: React.ReactNode;
}

const TabGuardContext = createContext<TabGuardContextValue | undefined>(undefined);

export const useTabGuardContext = () => {
    const context = useContext(TabGuardContext);
    if (!context) {
        throw new Error("useTabGuardContext must be used within TabGuardProvider");
    }
    return context;
};

export const TabGuardProvider = ({ children }: TabGuardProviderProps) => {
    const { t } = useTranslation();
    const tabStatesRef = useRef<Map<string | null, TabState>>(new Map());
    const [dialogOpen, setDialogOpen] = useState(false);
    const [pendingSwitch, setPendingSwitch] = useState<{
        fromView: string | null;
        toView: string | null;
        resolve: (value: boolean) => void;
    } | null>(null);

    const registerTab = useCallback((viewId: string | null, isDirty: boolean, saveHandler?: () => Promise<void>) => {
        tabStatesRef.current.set(viewId, { isDirty, saveHandler });
    }, []);

    const unregisterTab = useCallback((viewId: string | null) => {
        tabStatesRef.current.delete(viewId);
    }, []);

    const checkBeforeSwitch = useCallback((fromView: string | null, toView: string | null): Promise<boolean> => {
        const currentTabState = tabStatesRef.current.get(fromView);

        // 如果當前分頁沒有未保存的變更，直接允許切換
        if (!currentTabState?.isDirty) {
            return Promise.resolve(true);
        }

        // 顯示確認對話框並返回 Promise
        return new Promise((resolve) => {
            setPendingSwitch({ fromView, toView, resolve });
            setDialogOpen(true);
        });
    }, []);

    // 攔截瀏覽器層級的離開（重新整理、關閉分頁、上一頁、直接改網址）：
    // 只要任一分頁有未儲存的變更，就觸發瀏覽器原生的離開確認提示，避免變更遺失。
    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            const hasUnsavedChanges = Array.from(tabStatesRef.current.values()).some((tab) => tab.isDirty);
            if (hasUnsavedChanges) {
                event.preventDefault();
                // 部分瀏覽器需要設定 returnValue 才會顯示確認提示（提示文字由瀏覽器控制，無法自訂）。
                event.returnValue = "";
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, []);

    const handleSaveAndSwitch = useCallback(async () => {
        if (!pendingSwitch) return;

        const currentTabState = tabStatesRef.current.get(pendingSwitch.fromView);
        if (currentTabState?.saveHandler) {
            try {
                await currentTabState.saveHandler();
                setDialogOpen(false);
                pendingSwitch.resolve(true);
                setPendingSwitch(null);
            } catch (error) {
                // 保存失敗，不關閉對話框，不切換分頁
                customToast.error((error as unknown as { message: string }).message);
            }
        } else {
            // 沒有保存函數，直接切換
            setDialogOpen(false);
            pendingSwitch.resolve(true);
            setPendingSwitch(null);
        }
    }, [pendingSwitch]);

    const handleDiscardAndSwitch = useCallback(() => {
        if (!pendingSwitch) return;
        setDialogOpen(false);
        pendingSwitch.resolve(true);
        setPendingSwitch(null);
    }, [pendingSwitch]);

    const handleCancel = useCallback(() => {
        if (!pendingSwitch) return;
        setDialogOpen(false);
        pendingSwitch.resolve(false);
        setPendingSwitch(null);
    }, [pendingSwitch]);

    const contextValue = useMemo(
        () => ({
            registerTab,
            unregisterTab,
            checkBeforeSwitch,
        }),
        [registerTab, unregisterTab, checkBeforeSwitch]
    );

    return (
        <TabGuardContext.Provider value={contextValue}>
            {children}

            {/* 確認對話框 */}
            <Dialog
                open={dialogOpen}
                onClose={handleCancel}
                aria-labelledby='unsaved-changes-dialog-title'
                aria-describedby='unsaved-changes-dialog-description'
                sx={{
                    "& .MuiDialog-paper": {
                        borderRadius: "1.6rem",
                        padding: "2rem 2.8rem",
                    },
                }}
            >
                <DialogTitle id='unsaved-changes-dialog-title'>
                    {t("COMMON.UNSAVED_CHANGES_TITLE") || "您有未儲存的變更"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id='unsaved-changes-dialog-description'>
                        {t("COMMON.UNSAVED_CHANGES_MESSAGE") || "是否要在切換分頁前儲存變更？"}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSaveAndSwitch} variant='contained' color='primary' autoFocus>
                        {t("COMMON.SAVE_AND_SWITCH") || "保存並切換"}
                    </Button>
                    <Button onClick={handleDiscardAndSwitch} color='warning'>
                        {t("COMMON.DISCARD_AND_SWITCH") || "放棄並切換"}
                    </Button>
                    <Button onClick={handleCancel} color='inherit'>
                        {t("COMMON.Cancel") || "取消"}
                    </Button>
                </DialogActions>
            </Dialog>
        </TabGuardContext.Provider>
    );
};
